import { workspaceRoleLabel } from '@/utils/workspaceRoleLabel';
import { ENGAGE_ENABLED, ROUTE, UNIBOX_ENABLED } from '@/config';
import { BetaBadge, BetaDot } from './BetaBadge';
import WorkspaceSwitcher from '@/components/Workspace/WorkspaceSwitcher';
import { message } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  AnalyticsIcon,
  CouponIcon,
  DashboardIcon,
  FileIcon,
  FinanceIcon,
  OrdersIcon,
  PickupsIcon,
} from '../Icons';
import boxyLogo from '@/assets/boxy_logo.svg';
import boxyIcon from '@/assets/boxy_icon.svg';
import { useAuthStore } from '@/stores/authStore';
import { useBulkImportStore } from '@/stores';
import { getSidebarRouteInfo } from '@/utils';
import { useUserStore } from '@/stores';
import { selectUsesServices } from '@/stores/userStore';
import { useCustomerIO } from '@/hooks';
import { useQueryClient } from '@tanstack/react-query';
import usePermission from '@/hooks/usePermission';
import useOrdersPaused from '@/hooks/useOrdersPaused';
import { useFetchMyInvitations, useFetchServicePreferences } from '@/hooksApi';
import { PermissionName } from '@/types';
import {
  BookUser,
  ChevronRight,
  Gauge,
  LogOut,
  Package,
  Plus,
  ReceiptText,
  ShieldAlert,
  MessageSquare,
  Megaphone,
  Building2,
  ChevronsUpDown,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { RtlIcon } from '@/components/ui/rtl-icon';
import { cn } from '@/lib/utils';
import profileIcon from '@/assets/settings/profile.svg';
import locationsIcon from '@/assets/settings/locations.svg';
import teamIcon from '@/assets/settings/team.svg';
import accessTokensIcon from '@/assets/settings/access-tokens.svg';
import webhooksIcon from '@/assets/settings/webhooks.svg';

type SidebarItem = {
  key: string;
  label: string;
  disabled: boolean;
  renderIcon: (color: string) => JSX.Element;
  beta?: boolean;
};

/**
 * Enough of the number to recognise, not enough to read out.
 *
 * Keeps the country code and the last two digits: `9647701234567` → `+964…67`.
 * Short or malformed values are returned untouched rather than mangled into
 * something that looks like a different number.
 */
const maskPhone = (phone: string): string => {
  // `+964…01` was unrecognisable — it could be anyone's. Keeping the country
  // code, the operator prefix and the last four digits leaves a number the
  // owner recognises as theirs while the middle stays unreadable over a
  // shoulder: 9647700000901 → +964 770 ••• 0901.
  if (phone.length < 10) return phone;
  return `+${phone.slice(0, 3)} ${phone.slice(3, 6)} ••• ${phone.slice(-4)}`;
};

const Sidebar = () => {
  const clearToken = useAuthStore((state) => state.clearToken);
  const { user, setUser } = useUserStore((state) => state);
  const { clearPendingImport } = useBulkImportStore();

  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';
  const location = useLocation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const ordersPaused = useOrdersPaused();

  // The service catalogue is ops-configurable data. Until there is more than
  // one live service there is nothing to choose between, so the entry stays
  // hidden — which is also what keeps this feature invisible on deploy day.
  // The server returns OPEN invitations only, so a non-empty list IS the
  // pending count — the client filters nothing and must not start.
  const { data: myInvitations } = useFetchMyInvitations();
  const activeWorkspace = (user?.workspaces ?? []).find(
    (w) => w.uid === (user?.active_workspace ?? ''),
  );
  const pendingInvitations = myInvitations?.object?.length ?? 0;

  const { data: servicePreferences } = useFetchServicePreferences();
  const usesServices = useUserStore(selectUsesServices);
  const hasServiceChoice = usesServices && (servicePreferences?.services.length ?? 0) > 1;

  const { identifyUser } = useCustomerIO();
  const hasValidatedRef = useRef<string | null>(null);

  const [collapsed, setCollapsed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [railHover, setRailHover] = useState(false);
  const railRef = useRef<HTMLDivElement>(null);

  const handleLogOut = useCallback(() => {
    clearToken?.();
    setUser(undefined);
    clearPendingImport();
    navigate(ROUTE.LOGIN);
    queryClient.removeQueries(['user']);
  }, [clearToken, clearPendingImport, navigate, queryClient, setUser]);

  useEffect(() => {
    if (!user || !user.uid) return;

    if (hasValidatedRef.current === user.uid) return;
    hasValidatedRef.current = user.uid;

    identifyUser(user.uid, {
      phone: user.phone,
      user_type: 'MERCHANT',
      email: user.email,
      firstname: user.first_name,
      lastname: user.last_name,
      fullname: `${user.first_name}${user.last_name}`,
    });

    if (user.type !== 'MERCHANT') {
      message?.error(t('wrong_user_type'));
      handleLogOut();
    }
    if (!user.verified_at) {
      message?.error(t('user_not_verified'));
    }
  }, [user, identifyUser, handleLogOut, t]);

  const selectedTab = getSidebarRouteInfo(location.pathname);
  const activeTopNavKey =
    selectedTab?.name === 'Unknown' ? ROUTE.DASHBOARD : `/${selectedTab?.location}`;
  const isTicketCenterActive = location.pathname.startsWith(ROUTE.TICKET_CENTER);

  const topNavItems = useMemo<SidebarItem[]>(
    () => [
      {
        key: ROUTE.DASHBOARD,
        label: t('overview'),
        disabled: !hasPermission(PermissionName.DASHBOARD),
        renderIcon: (color) => <DashboardIcon width={18} height={18} color={color} />,
      },
      {
        key: ROUTE.ANALYTICS,
        label: t('analytics'),
        disabled: !hasPermission(PermissionName.DASHBOARD),
        renderIcon: (color) => <AnalyticsIcon width={18} height={18} color={color} />,
      },
      // UniBox — on everywhere (Beta, since 2026-09-24). Gate lives in
      // setup.config.ts; VITE_UNIBOX_ENABLED=false removes it with no code
      // change. Keep in step with the ROUTE.INBOX route.
      ...(UNIBOX_ENABLED
        ? [
            {
              key: ROUTE.INBOX,
              label: t('inbox'),
              disabled: !hasPermission(PermissionName.DASHBOARD),
              renderIcon: (color: string) => <MessageSquare width={18} height={18} color={color} />,
              // UniBox is in beta and may have bugs — say so where it is entered.
              beta: true,
            },
          ]
        : []),
      // Boxy Engage — off unless VITE_ENGAGE_ENABLED=true (setup.config.ts).
      ...(ENGAGE_ENABLED
        ? [
            {
              key: ROUTE.ENGAGE,
              label: t('engage'),
              disabled: !hasPermission(PermissionName.DASHBOARD),
              renderIcon: (color: string) => <Megaphone width={18} height={18} color={color} />,
              // Engage is in beta too (owner 2026-10-12) — same marker as UniBox.
              beta: true,
            },
          ]
        : []),
      {
        key: ROUTE.ORDERS,
        label: t('orders'),
        disabled: !hasPermission(PermissionName.ORDER_AND_SHIPMENT),
        renderIcon: (color) => <OrdersIcon width={18} height={18} color={color} />,
      },
      {
        key: ROUTE.PICKUPS,
        label: t('shipments'),
        disabled: !hasPermission(PermissionName.ORDER_AND_SHIPMENT),
        renderIcon: (color) => <PickupsIcon width={18} height={18} color={color} />,
      },
      {
        key: ROUTE.FINANCE,
        label: t('finance'),
        disabled: !hasPermission(PermissionName.FINANCE),
        renderIcon: (color) => <FinanceIcon width={18} height={18} color={color} />,
      },
      // Advance COD has no standalone sidebar entry — it lives inside the
      // Finance page as a tab (the old /advance-cod route redirects there).
      {
        key: ROUTE.CLAIMS,
        label: t('claims'),
        disabled: !hasPermission(PermissionName.VIEW_CLAIMS),
        renderIcon: (color) => <ShieldAlert width={18} height={18} color={color} />,
      },
    ],
    [t, hasPermission],
  );

  const accountItems = useMemo(
    () => [
      {
        title: t('profile'),
        node: <img src={profileIcon} alt='' className='h-5 w-5 shrink-0' />,
        path: ROUTE.SETTINGS_PROFILE,
      },
      {
        title: t('location'),
        node: <img src={locationsIcon} alt='' className='h-5 w-5 shrink-0' />,
        path: ROUTE.SETTINGS_LOCATIONS,
      },
      {
        title: t('team_members'),
        node: <img src={teamIcon} alt='' className='h-5 w-5 shrink-0' />,
        path: ROUTE.SETTINGS_TEAM_MEMBERS,
      },
      {
        title: t('file'),
        node: <FileIcon width={20} height={20} />,
        path: ROUTE.SETTINGS_FILES,
      },
      {
        title: t('address_book'),
        node: <BookUser size={20} className='shrink-0' />,
        path: ROUTE.SETTINGS_ADDRESS_BOOK,
      },
      {
        title: t('product_catalog'),
        node: <Package size={20} className='shrink-0' />,
        path: ROUTE.SETTINGS_PRODUCT_CATALOG,
      },
      // ORDER_AND_SHIPMENT is what the backend gates `service-preferences.*` on,
      // and what AccountCard already checks. Without it here a sub-user who
      // cannot hold the permission still saw the entry and was bounced by the
      // route guard on click.
      ...(hasServiceChoice && hasPermission(PermissionName.ORDER_AND_SHIPMENT)
        ? [
            {
              title: t('service_level'),
              node: <Gauge size={20} className='shrink-0' />,
              path: ROUTE.SETTINGS_SERVICE_PREFERENCE,
            },
          ]
        : []),
      /*
       * Delta 7 — ALWAYS PRESENT, badged when something is pending.
       *
       * The earlier version appeared only while an invitation was open, which
       * made the page findable exactly when it was urgent and unfindable every
       * other time: a merchant who wanted to see their workspaces, or invite
       * somebody, had nowhere to go. The badge carries the urgency; the entry
       * carries the destination, and they are different jobs.
       *
       * Not permission-gated: an invitee usually holds nothing in the workspace
       * inviting them, which is what an invitation IS.
       */
      {
        title:
          pendingInvitations > 0
            ? `${t('workspace.plural')} (${pendingInvitations})`
            : String(t('workspace.plural')),
        // Building2, not a mail icon: the destination is the merchant's
        // workspaces; invitations are one section on it.
        node: <Building2 size={16} className='shrink-0 text-sd-muted-foreground' />,
        path: ROUTE.SETTINGS_WORKSPACES,
      },
      // Shown to everyone who can read finance, NOT only to merchants who
      // hold a tier. "You are on Boxy's standard pricing" is a real answer a
      // merchant is entitled to look up, and gating the entry on having a plan
      // would put this surface right back where UI-F4 found it — implemented
      // and unreachable. The backend gates the route on FINANCE too.
      ...(hasPermission(PermissionName.FINANCE)
        ? [
            {
              title: t('pricing_plan'),
              node: <ReceiptText size={20} className='shrink-0' />,
              path: ROUTE.SETTINGS_PRICING_PLAN,
            },
          ]
        : []),
    ],
    [t, hasServiceChoice, hasPermission],
  );

  const integrationItems = useMemo(
    () => [
      {
        title: t('access_tokens'),
        node: <img src={accessTokensIcon} alt='' className='h-5 w-5 shrink-0' />,
        path: ROUTE.SETTINGS_ACCESS_TOKENS,
      },
      {
        title: t('web_hooks'),
        node: <img src={webhooksIcon} alt='' className='h-5 w-5 shrink-0' />,
        path: ROUTE.SETTINGS_WEBHOOKS,
      },
    ],
    [t],
  );

  // Merchant Orders Pause — ANDed into the existing permission boolean rather
  // than added as a second branch, so the greyed-out styling this CTA already
  // has for "no permission" is reused verbatim.
  const canCreateOrder = hasPermission(PermissionName.CREATE_ORDER) && !ordersPaused;
  const canOpenTicketCenter = hasPermission(PermissionName.DASHBOARD);

  return (
    <div className='sd-scope sd-font hidden md:flex relative m-4 ltr:mr-0 rtl:ml-0 sticky top-4 self-start z-20'>
      <aside
        className={cn(
          `flex h-[calc(100vh-32px)] flex-col border bg-sd-card rounded-2xl shadow-sm transition-all duration-300`,
          railHover
            ? 'ltr:border-r-2 ltr:border-r-sd-primary rtl:border-l-2 rtl:border-l-sd-primary border-sd-border'
            : 'border-sd-border',
          collapsed ? 'w-[64px]' : 'w-[210px]',
        )}
      >
        {/*
          🔴🔴 THE LOGO IS NEVER DISPLACED. It is the first element of the
          sidebar in both rails, and it does not move for anything.

          P5.1 put the workspace selector in the LOGO SLOT and hid the brand
          mark whenever a merchant had two workspaces — the code said so
          outright ("the logo and the switcher can never both be visible").
          The owner rejected it on sight, and every one of the 1769 dashboard
          tests passed while it was gone, because a test cannot see a missing
          logo. The switcher now sits UNDER the logo, where the approved brief
          puts it, and nothing is conditional about the brand.
        */}
        <div className='flex justify-center pt-6 pb-3 items-center overflow-hidden'>
          <Link
            to={ROUTE.ORDERS}
            className='flex items-center justify-center overflow-hidden'
            aria-label='Go to orders'
          >
            <img
              src={boxyIcon}
              alt='Boxy'
              className={cn(
                'shrink-0 transition-all duration-300',
                collapsed ? 'w-8 h-8' : 'w-0 h-8 opacity-0',
              )}
            />
            <img
              src={boxyLogo}
              alt='Boxy'
              className={cn(
                'shrink-0 transition-all duration-300',
                collapsed ? 'w-0 opacity-0' : 'w-[86px] opacity-100',
              )}
            />
          </Link>
        </div>

        {/*
          The workspace card, under the logo. EVERY merchant sees it, including
          single-workspace ones — their menu shows their one workspace with a
          check and the New workspace item, which is how someone learns a second
          store is possible at all.
        */}
        <div className='px-2 pb-4'>
          <WorkspaceSwitcher collapsed={collapsed} />
        </div>

        <div className='border-b border-sd-border' />

        <div className='px-2 py-4'>
          <NavLink
            to={ROUTE.ADDNEWORDER}
            onClick={(e) => {
              if (!canCreateOrder) e.preventDefault();
            }}
            className={cn(
              'flex items-center rounded-lg py-2.5 text-sm font-medium transition-all duration-300 bg-sd-primary text-sd-primary-foreground overflow-hidden',
              collapsed ? 'justify-center px-2.5 gap-0' : 'justify-center gap-2 px-3',
              canCreateOrder ? 'hover:bg-sd-primary/90' : 'opacity-40 cursor-not-allowed',
            )}
          >
            <span className='text-xl font-medium leading-none shrink-0'>+</span>
            <span
              className={cn(
                'whitespace-nowrap transition-all duration-300',
                collapsed
                  ? 'w-0 opacity-0 overflow-hidden delay-0'
                  : 'w-auto opacity-100 delay-150',
              )}
            >
              {t('new_order')}
            </span>
          </NavLink>
        </div>

        <div className='border-b border-sd-border' />

        <nav className='flex flex-1 flex-col gap-1 px-2 mt-2'>
          {topNavItems.map((item) => {
            const isActive = activeTopNavKey === item.key;
            const iconColor = item.disabled ? '#A8A8B3' : '#38383E';
            return (
              <NavLink
                key={item.key}
                to={item.key}
                onClick={(e) => {
                  if (item.disabled) e.preventDefault();
                }}
                title={
                  item.beta
                    ? `${item.label} (${String(t('beta'))}) — ${String(t('beta_hint'))}`
                    : item.label
                }
                className={cn(
                  'group flex items-center rounded-lg py-2.5 text-sm font-medium transition-colors',
                  collapsed ? 'justify-center px-0' : 'gap-3 px-3',
                  isActive
                    ? 'bg-sd-primary text-sd-primary-foreground'
                    : 'text-sd-foreground hover:bg-sd-sidebar-accent',
                  item.disabled && 'opacity-40 cursor-not-allowed',
                )}
              >
                <span className='relative flex'>
                  {item.renderIcon(iconColor)}
                  {item.beta && collapsed ? <BetaDot active={isActive} /> : null}
                </span>
                <span
                  className={cn(
                    'whitespace-nowrap transition-all duration-300',
                    collapsed ? 'w-0 opacity-0 overflow-hidden' : 'w-auto opacity-100',
                  )}
                >
                  {item.label}
                </span>
                {item.beta && !collapsed ? <BetaBadge active={isActive} /> : null}
              </NavLink>
            );
          })}
        </nav>

        <div className='border-t border-sd-sidebar-border p-3'>
          <button
            onClick={() => {
              if (canOpenTicketCenter) navigate(ROUTE.TICKET_CENTER);
            }}
            className={cn(
              'flex w-full items-center rounded-lg py-2.5 text-sm font-medium transition-colors',
              collapsed ? 'justify-center px-0' : 'gap-3 px-3',
              isTicketCenterActive
                ? 'bg-sd-primary text-sd-primary-foreground'
                : 'text-sd-foreground hover:bg-sd-sidebar-accent',
              !canOpenTicketCenter && 'opacity-40 cursor-not-allowed',
            )}
          >
            <CouponIcon color={!canOpenTicketCenter ? '#A8A8B3' : undefined} />
            <span
              className={cn(
                'whitespace-nowrap transition-all duration-300',
                collapsed ? 'w-0 opacity-0 overflow-hidden' : 'w-auto opacity-100',
              )}
            >
              {t('ticket_center')}
            </span>
          </button>

          <div className='border-b border-sd-sidebar-border my-3' />

          {/*
            Delta 7 — REBUILT AS A DROPDOWNMENU.

            The hand-rolled portal measured its own coordinates, reimplemented
            outside-click and Escape, and sized itself to the sidebar's width —
            228px holding 252px of content, so every trailing chevron was
            CLIPPED at the right edge. Radix positions, traps focus, closes on
            Escape and flips under RTL for free, and the chevrons are gone
            because they were decoration that caused the overflow.
          */}
          <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen} dir={isRtl ? 'rtl' : 'ltr'}>
            <DropdownMenuTrigger asChild>
              <button
                type='button'
                // A real accessible name: the previous trigger was a bare div
                // that tests had to find by CSS class, which is the brittleness
                // that broke them the moment it was rebuilt.
                aria-label={String(t('account'))}
                className={cn(
                  'flex w-full items-center rounded-lg py-2 transition-colors hover:bg-sd-muted data-[state=open]:bg-sd-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-sd-primary',
                  collapsed ? 'justify-center px-0' : 'gap-3 px-2',
                )}
              >
                <span className='relative shrink-0'>
                  {/* ROUND for a person, SQUARE for a workspace — the two must
                      never be confusable at a glance. */}
                  <span className='flex h-8 w-8 items-center justify-center rounded-full bg-sd-muted text-xs font-semibold'>
                    {((user?.first_name?.[0] ?? '') + (user?.last_name?.[0] ?? '')).toUpperCase() ||
                      '?'}
                  </span>
                  {pendingInvitations > 0 && (
                    <span
                      aria-label={`${t('workspace.invitations')} (${pendingInvitations})`}
                      className='absolute -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-sd-destructive px-1 text-[10px] font-semibold leading-none text-sd-destructive-foreground ring-2 ring-sd-card ltr:-right-1 rtl:-left-1'
                    >
                      {pendingInvitations > 9 ? '9+' : pendingInvitations}
                    </span>
                  )}
                </span>

                {!collapsed && (
                  <>
                    <span className='flex min-w-0 flex-col text-start'>
                      <span className='truncate text-sm font-medium leading-5 text-sd-foreground'>
                        {(user?.first_name ?? '') + ' ' + (user?.last_name ?? '')}
                      </span>
                      <span className='truncate text-xs leading-4 text-sd-muted-foreground'>
                        {/* The same label the switcher renders — one role must
                            not read as "Owner" in one place and "owner" two
                            inches below it. */}
                        {activeWorkspace
                          ? workspaceRoleLabel(activeWorkspace, t)
                          : (user?.roles?.[0] ?? user?.type?.toLowerCase() ?? '')}
                      </span>
                    </span>
                    <ChevronsUpDown className='ms-auto h-4 w-4 shrink-0 text-sd-muted-foreground' />
                  </>
                )}
              </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
              side='top'
              align='start'
              sideOffset={6}
              className='max-h-[calc(100vh-24px)] w-64 overflow-y-auto rounded-[10px] border-sd-border p-1 shadow-lg sd-scope sd-font data-[state=open]:duration-150 data-[state=closed]:duration-100 ease-out motion-reduce:data-[state=open]:zoom-in-100 motion-reduce:data-[state=closed]:zoom-out-100 motion-reduce:data-[state=open]:slide-in-from-top-0 motion-reduce:data-[state=closed]:slide-out-to-top-0'
            >
              {/*
                WHO, and WHERE. The top of the sidebar answers "which
                workspace"; this answers "which person", and the muted line
                joins them so a merchant with several stores can tell both
                without opening anything else.

                The phone is MASKED: this menu opens over a warehouse screen as
                often as a private one, and the full number buys nothing here —
                it only has to be recognisable as theirs.
              */}
              <div className='flex items-center gap-2.5 px-2 py-2'>
                <span className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sd-muted text-xs font-semibold'>
                  {((user?.first_name?.[0] ?? '') + (user?.last_name?.[0] ?? '')).toUpperCase() ||
                    '?'}
                </span>
                <span className='min-w-0 flex-1'>
                  <span className='block truncate text-sm font-medium leading-5 text-sd-foreground'>
                    {(user?.first_name ?? '') + ' ' + (user?.last_name ?? '')}
                  </span>
                  {user?.phone && (
                    <bdi dir='ltr' className='block truncate text-xs text-sd-muted-foreground'>
                      {maskPhone(user.phone)}
                    </bdi>
                  )}
                  {activeWorkspace && (
                    <span className='block truncate text-xs text-sd-muted-foreground'>
                      {String(
                        t('workspace.in_as', {
                          workspace: activeWorkspace.name ?? '',
                          role: workspaceRoleLabel(activeWorkspace, t),
                        }),
                      )}
                    </span>
                  )}
                </span>
              </div>

              <DropdownMenuSeparator className='my-1' />

              <DropdownMenuLabel className='px-2 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wide text-sd-muted-foreground'>
                {t('account')}
              </DropdownMenuLabel>
              {accountItems.map((item) => (
                /*
                  `asChild` + NavLink, not onSelect+navigate. A menu row that
                  navigates from a click handler is a button wearing a link's
                  job: no middle-click, no ⌘-click into a new tab, no "copy link
                  address", and nothing in the accessibility tree saying where
                  it goes. Radix keeps its keyboard behaviour either way.
                */
                <DropdownMenuItem key={item.path} asChild className='h-9 gap-2.5 rounded-md px-2'>
                  <NavLink
                    to={item.path}
                    /* The app has a global `a { color: #1677ff }`, which an
                       asChild link inherits — every row rendered in anchor
                       blue. Menu items are foreground-coloured everywhere else
                       in the product. */
                    className='text-sd-foreground no-underline hover:no-underline'
                  >
                    {item.node}
                    <span className='flex-1 truncate text-start'>{item.title}</span>
                  </NavLink>
                </DropdownMenuItem>
              ))}

              <DropdownMenuSeparator className='my-1' />

              <DropdownMenuLabel className='px-2 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wide text-sd-muted-foreground'>
                {t('integration')}
              </DropdownMenuLabel>
              {integrationItems.map((item) => (
                <DropdownMenuItem key={item.path} asChild className='h-9 gap-2.5 rounded-md px-2'>
                  <NavLink
                    to={item.path}
                    /* The app has a global `a { color: #1677ff }`, which an
                       asChild link inherits — every row rendered in anchor
                       blue. Menu items are foreground-coloured everywhere else
                       in the product. */
                    className='text-sd-foreground no-underline hover:no-underline'
                  >
                    {item.node}
                    <span className='flex-1 truncate text-start'>{item.title}</span>
                  </NavLink>
                </DropdownMenuItem>
              ))}

              {/* No "ACTION" label: one item does not need a section heading,
                  and the separator already says "this one is different". */}
              <DropdownMenuSeparator className='my-1' />

              <DropdownMenuItem
                onSelect={handleLogOut}
                className='h-9 gap-2.5 rounded-md px-2 text-sd-destructive focus:text-sd-destructive'
              >
                <LogOut className='h-4 w-4 shrink-0' />
                <span className='flex-1 text-start'>{t('log_out')}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <div
        ref={railRef}
        onClick={() => setCollapsed((prev) => !prev)}
        onMouseEnter={() => setRailHover(true)}
        onMouseLeave={() => setRailHover(false)}
        role='button'
        tabIndex={0}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-expanded={!collapsed}
        className='absolute ltr:right-0 rtl:left-0 top-0 h-full w-[20px] ltr:translate-x-[10px] rtl:-translate-x-[10px] cursor-pointer z-10 group flex items-center justify-center'
      >
        <div className='h-full w-[3px]' />
      </div>
    </div>
  );
};

export default Sidebar;
